<template>
    <div class="exploded-view-wrapper h-100 w-100 position-relative rounded-4 overflow-hidden border shadow-inner">
        <!-- Floating Editor Toolbar (Refined Precision) -->
        <div class="view-controls">
            <div
class="shadow-sm bg-white-glass rounded-pill border d-flex gap-0 align-items-center px-1 py-1"
                style="backdrop-filter: blur(8px); background: rgba(255, 255, 255, 0.9);">
                <!-- Navigation -->
                <ButtonPrime
v-tooltip.top="'Zoom In'" icon="fa fa-search-plus" variant="text" rounded severity="primary"
                    size="small" @click="zoomIn" />
                <ButtonPrime
v-tooltip.top="'Zoom Out'" icon="fa fa-search-minus" variant="text" rounded severity="primary"
                    size="small" @click="zoomOut" />

                <div class="mx-1 border-start" style="height: 18px; opacity: 0.3;"></div>

                <!-- Editor Actions -->
                <ButtonPrime
v-tooltip.top="isEditMode ? 'Lock Layout' : 'Unlock to Edit'" :icon="isEditMode ? 'fas fa-lock-open' : 'fas fa-lock'"
                    variant="text" rounded :severity="isEditMode ? 'warning' : 'primary'" size="small"
                    @click="isEditMode = !isEditMode" />

                <span v-if="isEditMode && saving" class="text-muted ms-2 d-inline-flex align-items-center" style="font-size: 11px;">
                    <i class="fas fa-spinner fa-spin me-1"></i> Saving...
                </span>
            </div>
        </div>

        <VueFlow
v-model:nodes="nodes" v-model:edges="edges" :fit-view-on-init="true" class="hotspot-canvas"
            :min-zoom="0.1" :max-zoom="4" :nodes-draggable="isEditMode" :nodes-selectable="true" :elements-selectable="true" :pan-on-drag="true"
            @node-click="onNodeClick" @pane-click="onPaneClick" @node-drag-stop="onNodeDragStop">
            <!-- 1. Anchor Node (Titik kecil) -->
            <template #node-anchor>
                <div class="anchor-point position-relative" :class="{ 'editable': isEditMode }">
                    <div class="anchor-dot"></div>
                    <Handle type="source" :position="Position.Right" :style="handleCenterStyle" />
                </div>
            </template>

            <!-- 2. Callout Node (Nomor saja) -->
            <template #node-callout="{ data, id }">
                <div class="position-relative d-flex align-items-center justify-content-center">
                    <div class="callout-number" :class="{ 'active': data.selected, 'editable': isEditMode }">
                        <Handle type="target" :position="Position.Left" :style="handleCenterStyle" />
                        {{ data.number || '?' }}
                    </div>
                    <i
v-if="isEditMode" class="fa fa-trash-alt delete-btn text-danger position-absolute"
                        style="left: 100%; margin-left: 8px;" @click.stop="removeMarker(id)"></i>
                </div>
            </template>

            <!-- Custom Edge: garis lurus dari as ke as -->
            <template #edge-center-line="{ sourceNode, targetNode, sourceX, sourceY, targetX, targetY }">
                <path
v-if="sourceNode && targetNode" 
                    :d="`M ${sourceNode.position.x + 6},${sourceNode.position.y + 6} L ${targetNode.position.x + 13},${targetNode.position.y + 13}`"
                    stroke="#5e72e4" stroke-width="1.5" stroke-linecap="round" fill="none" />
                <path
v-else :d="`M ${sourceX},${sourceY} L ${targetX},${targetY}`"
                    stroke="#5e72e4" stroke-width="1.5" stroke-linecap="round" fill="none" />
            </template>

            <!-- 3. Background Image Node -->
            <template #node-background="{ data }">
                <div class="base-image-node">
                    <img :src="getImageUrl(data.image)" class="technical-drawing" draggable="false">
                </div>
            </template>

            <Background pattern-color="#e2e8f0" :gap="25" />
        </VueFlow>

        <!-- Instructions (Compact Style) -->
        <div class="view-hint p-2 px-3">
            <div
v-if="isEditMode"
                class="d-inline-flex align-items-center bg-warning-subtle text-warning-emphasis px-2 py-1 rounded-2 border border-warning-subtle shadow-sm animate__animated animate__pulse animate__infinite"
                style="font-size: 10px; font-weight: 700;">
                <i class="fa fa-pencil-alt me-1"></i> EDITOR MODE: CLICK IMAGE TO PLACE, DRAG LABEL
            </div>
            <small v-else class="text-muted" style="font-size: 10px;"><i class="fa fa-info-circle me-1"></i> Interactive
                View</small>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import { VueFlow, useVueFlow, Position } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import config from '@/configs/config';
import assetsManagementService from '@/services/assetsManagement/assetsManagementService';
import api from '@/helpers/api';
import { getCurrentInstance } from 'vue';

const props = defineProps({
    asset: { type: Object, required: true },
    parts: { type: Array, default: () => [] },
    selectedPart: { type: Object, default: null }
});

const emit = defineEmits(['select-part', 'markers-saved']);
const { fitView, zoomIn, zoomOut, project } = useVueFlow();
const vm = getCurrentInstance();

const isEditMode = ref(false);
const saving = ref(false);
const nodes = ref([]);
const edges = ref([]);
let nextMarkerNumber = 1;

// Handle diletakkan persis di titik tengah secara matematis 1x1 piksel
// Agar garis dapat terhubung (konek) secara dinamis 360 derajat darimana saja
const handleCenterStyle = {
    opacity: 0,
    top: '50%',
    left: '50%',
    width: '1px',
    height: '1px',
    marginLeft: '-0.5px',
    marginTop: '-0.5px',
    minWidth: '0px',
    minHeight: '0px',
    transform: 'none',
    border: 'none',
    position: 'absolute',
    pointerEvents: 'none'
};

const getImageUrl = (path) => {
    if (!path) return "";
    return `${config.storageUrl}/${path}`;
};

// Build initially from props
const buildDiagram = () => {
    const newNodes = [];
    const newEdges = [];

    newNodes.push({
        id: 'bg-image',
        type: 'background',
        data: { image: props.asset.image },
        position: { x: 0, y: 0 },
        draggable: false, selectable: false, zIndex: -1
    });

    const existingMarkers = props.asset?.markers || [];
    let maxNumber = 0;

    existingMarkers.forEach((m) => {
        const partIndex = props.parts.findIndex(p => p.asset_code === m.asset_code);
        const ax = parseFloat(m.anchor_x) || 0;
        const ay = parseFloat(m.anchor_y) || 0;
        let lx = parseFloat(m.label_x) || 0;
        let ly = parseFloat(m.label_y) || 0;
        if (lx === 0 && ly === 0 && ax === 0 && ay === 0) {
            const offsetIndex = newNodes.filter(n => n.type === 'callout').length;
            lx = 220 + offsetIndex * 60;
            ly = 40 + offsetIndex * 50;
        }
        createMarkerNodes(newNodes, newEdges, {
            number: m.marker_number,
            partIndex: partIndex >= 0 ? partIndex : null,
            anchor: { x: ax, y: ay },
            label: { x: lx, y: ly },
            materialAssetCode: m.asset_code,
        });
        if (m.marker_number > maxNumber) maxNumber = m.marker_number;
    });

    nextMarkerNumber = maxNumber + 1;
    nodes.value = newNodes;
    edges.value = newEdges;
};

let markerUid = 0;
const createMarkerNodes = (nodeList, edgeList, { number, partIndex, anchor, label, materialAssetCode }) => {
    markerUid += 1;
    const safeMaterial = (materialAssetCode || 'orphan').replace(/[^a-zA-Z0-9_-]/g, '_');
    const id = `${safeMaterial}-n${number}-${markerUid}`;
    const anchorId = `anchor-${id}`;
    const labelId = `label-${id}`;

    nodeList.push({
        id: anchorId,
        type: 'anchor',
        data: { number },
        position: anchor,
        zIndex: 5,
    });

    nodeList.push({
        id: labelId,
        type: 'callout',
        data: { number, partIndex, materialAssetCode, selected: false },
        position: label,
        selectable: true,
        zIndex: 10,
    });

    edgeList.push({
        id: `edge-${id}`,
        source: anchorId,
        target: labelId,
        type: 'center-line',
        zIndex: 2,
    });
};

const removeMarker = (nodeId) => {
    const suffix = nodeId.replace(/^(anchor|label)-/, '');
    nodes.value = nodes.value.filter(n => !n.id.endsWith(suffix));
    edges.value = edges.value.filter(e => !e.id.endsWith(suffix));
};

const onNodeClick = ({ node }) => {
    if (!node.id.startsWith('label-')) return;
    nodes.value = nodes.value.map(n => {
        if (n.type === 'callout' && n.data) {
            return { ...n, data: { ...n.data, selected: (n.id === node.id) } };
        }
        return n;
    });
    if (node.data.partIndex !== null && node.data.partIndex !== undefined) {
        emit('select-part', props.parts[node.data.partIndex]);
    }
};

const onPaneClick = (event) => {
    if (!isEditMode.value) return;
    const point = project({ x: event.clientX, y: event.clientY });
    const number = nextMarkerNumber++;
    const partIndex = number - 1 < props.parts.length ? number - 1 : null;
    const part = partIndex !== null ? props.parts[partIndex] : null;
    createMarkerNodes(nodes.value, edges.value, {
        number,
        partIndex,
        anchor: point,
        label: { x: point.x + 180, y: point.y },
        materialAssetCode: part?.asset_code || null,
    });
};

const buildMarkerForMaterial = (materialCode) => {
    const labelById = new Map();
    const anchorById = new Map();
    nodes.value.forEach(n => {
        if (n.type === 'callout' && n.data?.materialAssetCode === materialCode) {
            labelById.set(n.id.replace('label-', ''), n);
        } else if (n.type === 'anchor') {
            anchorById.set(n.id.replace('anchor-', ''), n);
        }
    });
    const markers = [];
    labelById.forEach((label, key) => {
        const anchor = anchorById.get(key);
        if (!anchor) return;
        markers.push({
            marker_number: label.data.number,
            anchor_x: Math.round(anchor.position.x * 100) / 100,
            anchor_y: Math.round(anchor.position.y * 100) / 100,
            label_x: Math.round(label.position.x * 100) / 100,
            label_y: Math.round(label.position.y * 100) / 100,
        });
    });
    return markers.sort((a, b) => a.marker_number - b.marker_number);
};

const findMaterialCodeForNode = (nodeId) => {
    const suffix = nodeId.replace(/^(anchor|label)-/, '');
    const labelNode = nodes.value.find(n => n.id === `label-${suffix}`);
    return labelNode?.data?.materialAssetCode || null;
};

const persistMarker = async (materialCode) => {
    if (!materialCode) return;
    saving.value = true;
    try {
        const markers = buildMarkerForMaterial(materialCode);
        await api(vm.proxy, assetsManagementService.saveMarkers(materialCode, markers), true);
        emit('markers-saved', { materialCode, markers });
    } catch (e) {
        console.error('Failed to save marker:', e);
    } finally {
        saving.value = false;
    }
};

const onNodeDragStop = ({ node }) => {
    if (!isEditMode.value) return;
    if (!node.id.startsWith('anchor-') && !node.id.startsWith('label-')) return;
    const materialCode = findMaterialCodeForNode(node.id);
    persistMarker(materialCode);
};

watch(() => props.asset, () => {
    buildDiagram();
    setTimeout(() => fitView(), 200);
}, { deep: true });

watch(() => props.selectedPart, (newPart) => {
    nodes.value = nodes.value.map(n => {
        if (n.type === 'callout' && n.data) {
            return {
                ...n,
                data: {
                    ...n.data,
                    selected: newPart ? (n.data.materialAssetCode === newPart.asset_code) : false,
                },
            };
        }
        return n;
    });
}, { deep: true, immediate: true });

onMounted(() => {
    buildDiagram();
});
</script>

<style scoped>
.exploded-view-wrapper {
    background: #ffffff;
    height: 100%;
}

.view-controls {
    position: absolute;
    top: 15px;
    right: 15px;
    z-index: 100;
}

.hotspot-canvas {
    width: 100%;
    height: 100%;
}

.technical-drawing {
    display: block;
    max-width: 1500px;
}

/* Anchor Point */
.anchor-point {
    width: 12px;
    height: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.anchor-point.editable {
    cursor: move;
}

.anchor-dot {
    width: 12px;
    height: 12px;
    background: #5e72e4;
    border-radius: 50%;
    border: 2px solid #fff;
    box-shadow: 0 0 6px rgba(94, 114, 228, 0.4);
    box-sizing: border-box;
}

/* Callout (Nomor bulat) */
.callout-number {
    width: 26px;
    height: 26px;
    background: #8898aa;
    color: #fff;
    border-radius: 50%;
    font-size: 11px;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    border: 2px solid #fff;
    box-shadow: 0 2px 8px rgba(136, 152, 170, 0.4);
    position: relative;
    transition: all 0.3s ease;
}

.callout-number.editable {
    cursor: move;
    border-style: dashed;
    border-color: #8898aa;
}

.callout-number.active {
    background: #5e72e4;
    box-shadow: 0 2px 10px rgba(94, 114, 228, 0.6);
    transform: scale(1.15);
    z-index: 25;
}

.delete-btn {
    font-size: 12px;
    cursor: pointer;
    opacity: 0.6;
}

.delete-btn:hover {
    opacity: 1;
}


.view-hint {
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 100;
    pointer-events: none;
}

.bg-warning-soft {
    background: #fffbeb;
}
</style>